import { useState, useCallback, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type SortingState,
} from "@tanstack/react-table";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, Search, Download, Upload, X, ChevronDown } from "lucide-react";
import type { DataTableProps, ColumnFilterMeta, ColumnFilterOption } from "@/types";

/** Cache-bust stamp so production bundle hash updates after view-mode removal */
const DATA_TABLE_BUILD = "table-only-20260726d";

function getColumnFilterId(column: { id?: string; accessorKey?: string }) {
  return column.id || String(column.accessorKey || "");
}

function normalizeSelected(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (value === "" || value === undefined || value === null) return [];
  return [String(value)];
}

function ColumnFilter({
  filterId,
  meta,
  filters,
  onChange,
}: {
  filterId: string;
  meta: ColumnFilterMeta;
  filters: Record<string, any>;
  onChange: (filters: Record<string, any>) => void;
}) {
  if (!meta.filterVariant || meta.filterVariant === "multi-select") return null;

  const value = filters[filterId] ?? "";

  if (meta.filterVariant === "text") {
    return (
      <Input
        placeholder={meta.filterPlaceholder || "Filter..."}
        value={value}
        onChange={(e) => onChange({ ...filters, [filterId]: e.target.value })}
        className="h-7 text-xs bg-card"
      />
    );
  }

  if (meta.filterVariant === "select") {
    return (
      <Select value={value ? String(value) : "_all"} onValueChange={(v) => onChange({ ...filters, [filterId]: v === "_all" ? "" : v })}>
        <SelectTrigger className="h-7 text-xs bg-card">
          <SelectValue placeholder={meta.filterPlaceholder || "All"} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="_all">All</SelectItem>
          {(meta.filterOptions || []).map((opt) => (
            <SelectItem key={String(opt.value)} value={String(opt.value)}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (meta.filterVariant === "date-range") {
    const from = filters[`${filterId}_from`] ?? "";
    const to = filters[`${filterId}_to`] ?? "";
    return (
      <div className="flex gap-1">
        <Input
          type="date"
          value={from}
          onChange={(e) => onChange({ ...filters, [`${filterId}_from`]: e.target.value })}
          className="h-7 text-[10px] bg-card"
          title="From"
        />
        <Input
          type="date"
          value={to}
          onChange={(e) => onChange({ ...filters, [`${filterId}_to`]: e.target.value })}
          className="h-7 text-[10px] bg-card"
          title="To"
        />
      </div>
    );
  }

  return null;
}

function HeaderMultiSelectFilter({
  label,
  filterId,
  options,
  selected,
  onChange,
}: {
  label: string;
  filterId: string;
  options: ColumnFilterOption[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const active = selected.length > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-1 max-w-full rounded-md px-1.5 py-1 text-left hover:bg-muted",
            active && "bg-primary/10 text-primary",
          )}
          data-testid={`header-filter-${filterId}`}
        >
          <span className="truncate font-semibold text-base uppercase tracking-wider">{label}</span>
          {active && (
            <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] text-white">
              {selected.length}
            </span>
          )}
          <ChevronDown className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56 max-h-72 overflow-y-auto">
        <DropdownMenuLabel className="flex items-center justify-between gap-2">
          <span>Filter {label}</span>
          {active && (
            <button type="button" className="text-xs text-primary hover:underline" onClick={() => onChange([])}>
              Clear
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.length === 0 ? (
          <div className="px-2 py-3 text-xs text-muted-foreground">No values</div>
        ) : (
          options.map((opt) => {
            const checked = selected.includes(String(opt.value));
            return (
              <DropdownMenuCheckboxItem
                key={String(opt.value)}
                checked={checked}
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={(isChecked) => {
                  const value = String(opt.value);
                  if (isChecked) onChange([...selected, value]);
                  else onChange(selected.filter((v) => v !== value));
                }}
              >
                <span className="truncate">{opt.label}</span>
              </DropdownMenuCheckboxItem>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function DataTable<TData>({
  columns,
  data,
  totalPages = 1,
  currentPage = 1,
  onPageChange,
  searchValue = "",
  onSearchChange,
  columnFilters,
  onColumnFiltersChange,
  loading = false,
  onExport,
  onUpload,
  leftActions,
  headerActions,
  toolbarExtra,
  headerFilters = false,
  onRowClick,
  "data-testid": testId = "data-table",
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [freeformSearch, setFreeformSearch] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const hasColumnFilters = !!onColumnFiltersChange;
  const filtersValue = useMemo(() => columnFilters || {}, [columnFilters]);

  const hasAnyActiveFilter = Object.values(filtersValue).some((v) => {
    if (Array.isArray(v)) return v.length > 0;
    return v !== "" && v !== undefined && v !== null;
  });

  const handleFiltersChange = useCallback(
    (newFilters: Record<string, any>) => {
      if (!onColumnFiltersChange) return;
      onColumnFiltersChange(newFilters);
    },
    [onColumnFiltersChange],
  );

  const clearAllFilters = useCallback(() => {
    if (!onColumnFiltersChange) return;
    const cleared: Record<string, any> = {};
    Object.keys(filtersValue).forEach((k) => {
      cleared[k] = Array.isArray(filtersValue[k]) ? [] : "";
    });
    onColumnFiltersChange(cleared);
  }, [onColumnFiltersChange, filtersValue]);

  const table = useReactTable({
    data: data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    state: { sorting },
  });

  const filteredRows = useMemo(() => {
    const modelRows = table.getRowModel().rows;
    let rows = modelRows.length === 0 && Array.isArray(data) && data.length > 0 ? table.getCoreRowModel().rows : modelRows;

    const query = freeformSearch.trim().toLowerCase();
    if (query) {
      rows = rows.filter((row) => {
        const haystack = Object.values((row.original || {}) as Record<string, unknown>)
          .map((value) => String(value ?? ""))
          .join(" ")
          .toLowerCase();
        return haystack.includes(query);
      });
    }

    if (hasColumnFilters) {
      rows = rows.filter((row) => {
        for (const col of columns) {
          const meta = col.meta as ColumnFilterMeta | undefined;
          if (!meta?.filterVariant) continue;
          const filterId = getColumnFilterId(col);

          if (meta.filterVariant === "multi-select" || (headerFilters && meta.filterVariant)) {
            const selected = normalizeSelected(filtersValue[filterId]);
            if (selected.length === 0) continue;
            const accessor = meta.filterAccessor || filterId;
            const raw = (row.original as Record<string, unknown>)?.[accessor];
            if (!selected.includes(String(raw ?? ""))) return false;
            continue;
          }

          if (meta.filterVariant === "text") {
            const text = String(filtersValue[filterId] ?? "").trim().toLowerCase();
            if (!text) continue;
            const accessor = meta.filterAccessor || filterId;
            const raw = String((row.original as Record<string, unknown>)?.[accessor] ?? "").toLowerCase();
            if (!raw.includes(text)) return false;
          }

          if (meta.filterVariant === "select") {
            const selected = String(filtersValue[filterId] ?? "");
            if (!selected) continue;
            const accessor = meta.filterAccessor || filterId;
            if (String((row.original as Record<string, unknown>)?.[accessor] ?? "") !== selected) return false;
          }
        }
        return true;
      });
    }

    return rows;
  }, [table, data, freeformSearch, hasColumnFilters, columns, filtersValue, headerFilters]);

  const filterableColumns = useMemo(
    () =>
      columns.filter((col) => {
        const meta = col.meta as ColumnFilterMeta | undefined;
        return !!meta?.filterVariant && meta.filterVariant !== "multi-select";
      }),
    [columns],
  );

  const showFilterPanel = !headerFilters && hasColumnFilters && (filterableColumns.length > 0 || !!leftActions);

  const optionsForColumn = useCallback(
    (col: any, filterId: string, meta: ColumnFilterMeta): ColumnFilterOption[] => {
      if (meta.filterOptions?.length) return meta.filterOptions;
      const accessor = meta.filterAccessor || filterId;
      const seen = new Set<string>();
      const options: ColumnFilterOption[] = [];
      for (const row of data || []) {
        const raw = (row as Record<string, unknown>)?.[accessor];
        const value = String(raw ?? "").trim();
        if (!value || seen.has(value)) continue;
        seen.add(value);
        options.push({ label: value, value });
      }
      return options.sort((a, b) => a.label.localeCompare(b.label));
    },
    [data],
  );

  return (
    <div className="space-y-4" data-testid={testId} data-table-build={DATA_TABLE_BUILD}>
      {/* Toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 w-full sm:w-auto min-w-0">
          {!showFilterPanel && leftActions}
          {onSearchChange ? (
            <div className="relative flex-1 sm:flex-none sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchValue}
                onChange={(e) => onSearchChange?.(e.target.value)}
                className="pl-9 bg-card"
                data-testid={`${testId}-search`}
              />
            </div>
          ) : null}
          <Input
            placeholder="Type to filter..."
            value={freeformSearch}
            onChange={(e) => setFreeformSearch(e.target.value)}
            className="h-8 flex-1 sm:flex-none sm:w-[240px] bg-card"
            data-testid={`${testId}-quick-search`}
          />
          {showFilterPanel && (
            <Button variant="outline" size="sm" className="h-8 text-xs shrink-0" onClick={() => setFiltersOpen((prev) => !prev)}>
              Filters
              {hasAnyActiveFilter && (
                <span className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] text-white">!</span>
              )}
              <ChevronDown className={cn("ml-1 h-3.5 w-3.5 transition-transform", filtersOpen && "rotate-180")} />
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-nowrap w-full sm:w-auto sm:justify-end overflow-x-auto">
          {toolbarExtra}
          {hasAnyActiveFilter && (
            <Button variant="ghost" size="sm" onClick={clearAllFilters} className="h-8 text-xs">
              <X className="w-3 h-3 mr-1" /> Clear Filters
            </Button>
          )}
          {onExport && (
            <Button variant="outline" size="sm" onClick={onExport} data-testid={`${testId}-export-btn`}>
              <Download className="w-4 h-4 mr-1.5" /> Export
            </Button>
          )}
          {onUpload && (
            <Button variant="outline" size="sm" onClick={onUpload} data-testid={`${testId}-upload-btn`}>
              <Upload className="w-4 h-4 mr-1.5" /> Upload
            </Button>
          )}
          {headerActions}
        </div>
      </div>

      {showFilterPanel && filtersOpen && (
        <div className="border rounded-md bg-card text-card-foreground shadow-sm p-4" data-testid={`${testId}-filters`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Filters</p>
              {hasAnyActiveFilter && (
                <span className="text-[10px] font-medium uppercase tracking-wide text-primary bg-primary/10 px-2 py-0.5 rounded">Active</span>
              )}
            </div>
            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setFiltersOpen(false)} title="Collapse filters">
              <ChevronDown className="h-4 w-4 rotate-180" />
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {leftActions && (
              <div className="space-y-1.5">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Quick Filters</p>
                <div>{leftActions}</div>
              </div>
            )}
            {filterableColumns.map((col) => {
              const filterId = getColumnFilterId(col);
              const meta = col.meta as ColumnFilterMeta;
              const headerLabel = typeof col.header === "string" ? col.header : filterId || "Filter";
              return (
                <div key={`filter-${filterId}`} className="space-y-1.5">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{headerLabel}</p>
                  <ColumnFilter filterId={filterId} meta={meta} filters={filtersValue} onChange={handleFiltersChange} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="border rounded-md bg-card text-card-foreground overflow-hidden shadow-sm">
        <Table className="text-lg">
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id} className="bg-muted/50">
                {hg.headers.map((header) => {
                  const meta = header.column.columnDef.meta as ColumnFilterMeta | undefined;
                  const filterId = header.column.id;
                  const label = typeof header.column.columnDef.header === "string" ? header.column.columnDef.header : filterId;
                  const useHeaderFilter = headerFilters && meta?.filterVariant && filterId !== "actions";

                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        "font-semibold text-base uppercase tracking-wider",
                        meta?.hideOnMobile && "hidden md:table-cell",
                        meta?.columnClassName,
                      )}
                    >
                      {header.isPlaceholder ? null : useHeaderFilter ? (
                        <div className="flex items-center gap-1">
                          <HeaderMultiSelectFilter
                            label={label}
                            filterId={filterId}
                            options={optionsForColumn(header.column.columnDef, filterId, meta)}
                            selected={normalizeSelected(filtersValue[filterId])}
                            onChange={(next) => handleFiltersChange({ ...filtersValue, [filterId]: next })}
                          />
                          <button
                            type="button"
                            className="p-1 rounded hover:bg-muted"
                            onClick={header.column.getToggleSortingHandler()}
                            title="Sort"
                          >
                            <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
                          </button>
                        </div>
                      ) : (
                        <button className="flex items-center gap-1 hover:text-foreground" onClick={header.column.getToggleSortingHandler()}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
                        </button>
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center py-12 text-muted-foreground">
                  Loading...
                </TableCell>
              </TableRow>
            ) : filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center py-12 text-muted-foreground">
                  No records found
                </TableCell>
              </TableRow>
            ) : (
              filteredRows.map((row) => (
                <TableRow
                  key={row.id}
                  className={`hover:bg-muted/30 transition-colors${onRowClick ? " cursor-pointer" : ""}`}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        "text-lg py-3.5",
                        (cell.column.columnDef.meta as ColumnFilterMeta | undefined)?.hideOnMobile && "hidden md:table-cell",
                        (cell.column.columnDef.meta as ColumnFilterMeta | undefined)?.columnClassName,
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages}
          </p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => onPageChange?.(1)} disabled={currentPage <= 1}>
              <ChevronsLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => onPageChange?.(currentPage - 1)} disabled={currentPage <= 1}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => onPageChange?.(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => onPageChange?.(totalPages)} disabled={currentPage >= totalPages}>
              <ChevronsRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
